import React, { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../store/store';
import { useSignOut } from '../../core/identity-access/store/signOut';
import { PortalAccountLinks, PortalNavList } from '../../design-system/chrome';
import { PortalEnvironment } from '../../design-system/environments';
import { OpsHeader } from '../../design-system/ops-header';
import StepUpPrompt from '../../core/identity-access/components/StepUpPrompt';
import FamilyContextSwitcher, {
  FamilyContextIndicator,
} from '../../core/identity-access/components/FamilyContextSwitcher';

/**
 * The console chrome — `.shell / .top / .side` — used by the family and by
 * operations.
 *
 * A second shell rather than a parameterised first one, and deliberately: the
 * educator portal's header carries none of what a console's does. What the two
 * *consoles* share is real — the rail, the account links, sign-out — and what
 * they do not is the bar at the top, so the bar is chosen by the environment
 * and everything below it is written once.
 *
 * That split was overdue. Until now this file drew the family's bar for both,
 * and the family's class names (`.top-l`, `.acct`, `.chip`, `.icon-btn`) have
 * no rules at all under `.env-admin` — so operations rendered an unstyled
 * header while appearing, in code, to have one.
 */
interface Props {
  environment: PortalEnvironment;
}

/**
 * Below this width the rail becomes a drawer rather than collapsing.
 *
 * The prototype's own breakpoint, and the stylesheet's: `.env-admin.mobnav
 * .side` only slides in under a media query at 960px, so toggling the wrong
 * class here would appear to do nothing.
 */
const DRAWER_BREAKPOINT = 960;

/** Where the rail remembers whether it was collapsed. */
const RAIL_KEY = 'iqmaxx.console.rail';

/**
 * Read the remembered rail state.
 *
 * Wrapped, because site data can be blocked and a private window throws on
 * access. An operator whose browser refuses storage gets an expanded rail,
 * which is the right failure: they can still see every label.
 */
function rememberedRail(): boolean {
  try {
    return window.localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}

const ConsoleShell: React.FC<Props> = ({ environment }) => {
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);

  const [menuOpen, setMenuOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  /** Desktop: the rail collapses to icons. Mobile: it slides in as a drawer. */
  const [railCollapsed, setRailCollapsed] = useState(rememberedRail);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    // Menus and the mobile drawer close on navigation; the collapsed rail does
    // not. ADM-01-FR-06 asks for shell state to survive the trip into a record
    // and back, and a rail that springs open every time an operator opens a
    // case is the most visible way to fail that.
    setMenuOpen(false);
    setSideOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_KEY, railCollapsed ? '1' : '0');
    } catch {
      // A browser that will not store it still renders correctly; the
      // preference simply does not outlive the tab.
    }
  }, [railCollapsed]);

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'IQ';
  const fullName = user ? `${user.firstName} ${user.lastName}` : 'Cuenta';

  // PRT-17-FR-01 — the shared routine, so "Cerrar sesión" here revokes the
  // session server-side. It used to dispatch `logout()` alone, which left the
  // Cognito session valid on every other device for the family, teacher and
  // admin consoles alike.
  const signOut = useSignOut();

  /**
   * One button, two behaviours — the prototype's `sideToggle`.
   *
   * Measured at press time rather than tracked, because a rotated tablet
   * changes the answer and nothing here needs to re-render when it does.
   */
  const toggleSide = useCallback(() => {
    if (window.innerWidth <= DRAWER_BREAKPOINT) {
      setDrawerOpen((v) => !v);
    } else {
      setRailCollapsed((v) => !v);
    }
  }, []);

  const isOps = environment.consoleHeader === 'ops';

  // The rail and drawer rules are written against the scope element itself
  // (`.env-admin.rail .side`), so the modifiers belong here rather than on the
  // shell div.
  const scopeClasses = [
    environment.scope,
    isOps && railCollapsed ? 'rail' : '',
    isOps && drawerOpen ? 'mobnav' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={scopeClasses}>
      <div className={`shell${sideOpen ? ' side-open' : ''}`}>
        {isOps ? (
          <OpsHeader
            home={environment.home}
            badge={environment.badge}
            initials={initials}
            name={fullName}
            role={user?.role ?? 'Operaciones'}
            onToggleSide={toggleSide}
          />
        ) : (
          <div className="top">
            <div className="top-l">
              <button
                className="icon-btn ham"
                onClick={() => setSideOpen((v) => !v)}
                aria-label="Menu"
              >
                <span className="mi-ico">
                  <svg viewBox="0 0 24 24">
                    <line x1="3" y1="7" x2="17" y2="7" />
                    <line x1="3" y1="12" x2="17" y2="12" />
                    <line x1="3" y1="17" x2="14" y2="17" />
                  </svg>
                </span>
              </button>
              <Link to={environment.home} className="logo">
                i<span>Q</span>Maxx
              </Link>
              <span className="chip">{environment.badge}</span>
              {/* PRT-17-FR-02 — whose view this is, on every family route. */}
              <FamilyContextIndicator />
            </div>

            <div className="top-r">
              <button className="icon-btn" aria-label="Notificaciones">
                <span className="mi-ico">
                  <svg viewBox="0 0 24 24">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  <span className="dot" />
                </span>
              </button>

              <div
                className="acct"
                onClick={() => setMenuOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setMenuOpen((v) => !v);
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <div className="av">{initials}</div>
                <span className="ib-lb">{user?.firstName ?? 'Cuenta'}</span>
              </div>

              <div className={`menu${menuOpen ? ' show' : ''}`} role="menu">
                <div className="menu-head">
                  <div className="av">{initials}</div>
                  <div>
                    <div className="n">{fullName}</div>
                    <div className="e">{user?.email ?? ''}</div>
                  </div>
                </div>
                <PortalAccountLinks environment={environment} itemClass="mi" onSignOut={signOut} />
              </div>
            </div>
          </div>
        )}

        <div className="body">
          <nav className="side">
            {isOps ? (
              /* Operations puts its rail items directly in `.side` — there is
                 no `.side-in` under `.env-admin`, and wrapping them in one
                 leaves the rail without its padding. */
              <>
                <PortalNavList environment={environment} onNavigate={() => setDrawerOpen(false)} />
                <div className="slb">Cuenta</div>
                {/* The operations prototype draws no account menu at all. Rather
                    than invent one or leave the family's unstyled, the same
                    shared links render as rail items — styled by classes this
                    environment actually defines, and sign-out stays reachable. */}
                <PortalAccountLinks environment={environment} itemClass="nav" onSignOut={signOut} />
              </>
            ) : (
              <div className="side-in">
                {/* PRT-08-FR-07 — the console had no way to change learner; the
                    only switcher lived in MainLayout's sidebar. */}
                <FamilyContextSwitcher parentInitials={initials} />
                <PortalNavList environment={environment} onNavigate={() => setSideOpen(false)} />
              </div>
            )}
          </nav>

          {/* `#main` in operations, `#content` in the family console. Each is
              where its own sheet puts the scroll container and the gutters. */}
          <div id={isOps ? 'main' : 'content'}>
            {isOps ? <Outlet /> : <div className="scr on">{<Outlet />}</div>}
          </div>
        </div>
      </div>
      {/* DEC-A01-08. Renders nothing until the API asks for a challenge. */}
      <StepUpPrompt />
    </div>
  );
};

export default ConsoleShell;
